import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiPackage, FiArrowRight, FiRefreshCw, FiMapPin, 
  FiAlertTriangle, FiTruck, FiDollarSign, FiClock,
  FiCheck, FiX
} from 'react-icons/fi';

const ExpressServicesPage = () => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);

  // Scroll para o topo quando a página carrega
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      id: 'leva_traz',
      title: 'Leva e Traz',
      description: 'Buscamos seu item em um endereço e levamos para outro. Opção de ida e volta!',
      icon: FiRefreshCw,
      color: 'blue',
      features: [
        'Coleta no endereço de origem',
        'Entrega no destino',
        'Opção de retorno ao ponto inicial',
        'Ideal para documentos, chaves, etc.'
      ],
      pricing: {
        single: 20.00,
        roundTrip: 40.00
      }
    },
    {
      id: 'ponto_a_ponto',
      title: 'Ponto a Ponto',
      description: 'Entregas rápidas de pequenos itens de um ponto a outro da cidade.',
      icon: FiMapPin,
      color: 'green',
      features: [
        'Entrega direta',
        'Pequenos volumes',
        'Acompanhamento em tempo real',
        'Código de segurança'
      ],
      pricing: {
        single: 20.00
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <span className="text-2xl font-bold text-gray-900">BARUERI</span>
            <span className="text-2xl">⚡</span>
            <span className="text-2xl font-bold text-gray-900">FLASH</span>
          </div>
          <button 
            onClick={() => navigate('/login')}
            className="bg-red-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors"
          >
            Entrar
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-900 to-gray-800 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <FiTruck className="w-8 h-8 text-yellow-400" />
            <span className="text-yellow-400 font-semibold">Serviços Express</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Leva e Traz & Entregas Ponto a Ponto
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Precisa buscar ou entregar algo? Nossos motoboys fazem isso por você de forma rápida e segura!
          </p>
        </div>
      </section>

      {/* Aviso Importante */}
      <section className="bg-red-50 border-y border-red-200 py-4 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <FiAlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <p className="text-red-800 font-medium">
            <strong>Importante:</strong> NÃO transportamos alimentos, comida, refeições ou qualquer item perecível.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-12">
            Escolha o serviço ideal para você
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => {
              const Icon = service.icon;
              const isSelected = selectedService === service.id;
              const colorClasses = service.color === 'blue' 
                ? { bg: 'bg-blue-50', border: 'border-blue-500', icon: 'bg-blue-100 text-blue-600', btn: 'bg-blue-600 hover:bg-blue-700' }
                : { bg: 'bg-green-50', border: 'border-green-500', icon: 'bg-green-100 text-green-600', btn: 'bg-green-600 hover:bg-green-700' };

              return (
                <div 
                  key={service.id}
                  className={`bg-white rounded-2xl p-8 shadow-sm border-2 transition-all cursor-pointer ${
                    isSelected ? colorClasses.border : 'border-gray-100 hover:border-gray-200'
                  }`}
                  onClick={() => setSelectedService(service.id)}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${colorClasses.icon}`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    {isSelected && (
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <FiCheck className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-700 text-sm">
                        <FiCheck className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Pricing */}
                  <div className={`${colorClasses.bg} rounded-xl p-4 mb-6`}>
                    <div className="flex items-center gap-2 mb-2">
                      <FiDollarSign className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold text-gray-900">Valores:</span>
                    </div>
                    {service.id === 'leva_traz' ? (
                      <div className="space-y-1 text-sm">
                        <p className="text-gray-700">
                          <span className="font-medium">Só ida:</span> R$ {service.pricing.single.toFixed(2)}
                        </p>
                        <p className="text-gray-700">
                          <span className="font-medium">Ida e volta:</span> R$ {service.pricing.roundTrip.toFixed(2)}
                        </p>
                      </div>
                    ) : (
                      <p className="text-gray-700 text-sm">
                        <span className="font-medium">Por entrega:</span> R$ {service.pricing.single.toFixed(2)}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/express/solicitar?tipo=${service.id}`);
                    }}
                    className={`w-full py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-colors ${colorClasses.btn}`}
                  >
                    Solicitar {service.title}
                    <FiArrowRight className="w-5 h-5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-gray-100 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-12">
            Como Funciona?
          </h2>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: 1, title: 'Solicite', desc: 'Informe os endereços e descreva o item' },
              { step: 2, title: 'Pague', desc: 'Realize o pagamento via PIX' },
              { step: 3, title: 'Acompanhe', desc: 'Um motoboy aceita e você acompanha' },
              { step: 4, title: 'Receba', desc: 'Confirme com o código de segurança' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">
                  {item.step}
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Items we transport */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
            O que transportamos?
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* SIM */}
            <div className="bg-green-50 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <FiCheck className="w-6 h-6 text-green-600" />
                <h3 className="font-semibold text-green-800">Transportamos</h3>
              </div>
              <ul className="space-y-2 text-green-700">
                <li>✓ Documentos e contratos</li>
                <li>✓ Chaves e chaveiros</li>
                <li>✓ Pequenos eletrônicos</li>
                <li>✓ Roupas e acessórios</li>
                <li>✓ Medicamentos (sem receita controlada)</li>
                <li>✓ Presentes e encomendas</li>
                <li>✓ Livros e revistas</li>
                <li>✓ Peças pequenas</li>
              </ul>
            </div>

            {/* NÃO */}
            <div className="bg-red-50 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <FiX className="w-6 h-6 text-red-600" />
                <h3 className="font-semibold text-red-800">NÃO Transportamos</h3>
              </div>
              <ul className="space-y-2 text-red-700">
                <li>✗ Comida e alimentos</li>
                <li>✗ Bebidas</li>
                <li>✗ Produtos perecíveis</li>
                <li>✗ Itens ilegais</li>
                <li>✗ Produtos inflamáveis</li>
                <li>✗ Animais</li>
                <li>✗ Valores em dinheiro (grande quantidade)</li>
                <li>✗ Itens frágeis sem embalagem</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-900 py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Pronto para solicitar?
          </h2>
          <p className="text-gray-400 mb-6">
            Faça login ou cadastre-se para usar nossos serviços express
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/login')}
              className="bg-red-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-red-700 transition-colors"
            >
              Entrar na Conta
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="bg-white text-gray-900 px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Criar Conta
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-gray-400 py-6 px-4 text-center text-sm">
        <p>© 2025 Barueri Flash. Todos os direitos reservados.</p>
        <p className="mt-1">CNPJ: 37.192.230/0001-00 | WhatsApp: (11) 94772-6031</p>
      </footer>
    </div>
  );
};

export default ExpressServicesPage;
